import { CheckCircle2, Clock3, Coins, Lightbulb, Sparkles } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ImageWorkspace } from '../components/ImageWorkspace';
import { FreeImageWorkspace } from '../components/FreeImageWorkspace';
import { guides } from '../data/guides';
import { tools, type ToolSlug, toolMap } from '../data/tools';
import { breadcrumbSchema, Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';

const relatedMap: Partial<Record<ToolSlug, ToolSlug[]>> = {
  enhance: ['upscale', 'denoise', 'sharpen'],
  upscale: ['enhance', 'product-photo', 'sharpen'],
  restore: ['face-restore', 'denoise', 'upscale'],
  'face-restore': ['restore', 'enhance', 'upscale'],
  'remove-background': ['product-photo', 'enhance', 'relight'],
  deblur: ['denoise', 'enhance', 'upscale'],
  denoise: ['deblur', 'enhance', 'sharpen'],
  sharpen: ['enhance', 'deblur', 'upscale'],
  relight: ['enhance', 'product-photo', 'remove-background'],
  'product-photo': ['remove-background', 'upscale', 'relight']
};

export function ToolPage() {
  const { slug } = useParams();
  const tool = toolMap[slug as ToolSlug];
  if (!tool) return <Navigate to="/tools" replace />;
  const relatedSlugs = relatedMap[tool.slug] ?? tools.filter((item) => item.slug !== tool.slug && item.mode === tool.mode && item.category === tool.category).slice(0, 3).map((item) => item.slug);
  const related = relatedSlugs.map((relatedSlug) => toolMap[relatedSlug]).filter(Boolean);
  const relatedGuides = guides.filter((guide) => guide.relatedTool === tool.slug).slice(0, 10);
  const isFree = tool.mode === 'free';
  const Icon = tool.icon;

  const applicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: tool.name,
    url: absoluteUrl(`/tools/${tool.slug}`),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web browser',
    description: tool.seoDescription,
    offers: {
      '@type': 'Offer',
      price: isFree ? '0' : '9',
      priceCurrency: 'USD',
      description: isFree ? 'Free browser utility with no sign-in or AI credits required.' : 'Access is included with Nexhance AI account credits and subscription plans.'
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: tool.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer }
    }))
  };

  return (
    <section className="page-section tool-detail-page">
      <Seo
        title={tool.seoTitle}
        description={tool.seoDescription}
        path={`/tools/${tool.slug}`}
        jsonLd={[
          applicationSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'AI image tools', path: '/tools' },
            { name: tool.name, path: `/tools/${tool.slug}` }
          ])
        ]}
      />
      <div className="shell">
        <div className="tool-detail-hero">
          <div className="tool-detail-title">
            <span className="large-tool-icon"><Icon size={28} /></span>
            <div>
              <p className="eyebrow">{tool.eyebrow}</p>
              <h1>{tool.name}</h1>
            </div>
          </div>
          <div className="tool-detail-summary">
            <p className="tool-hero-description">{tool.description}</p>
            <div className="tool-detail-meta">
              <span><Coins size={16} /> {isFree ? 'Free · 0 credits' : `from ${tool.creditCost} credits`}</span>
              <span><Clock3 size={16} /> {tool.processingTime}</span>
              <span><CheckCircle2 size={16} /> {tool.formats.join(', ')}</span>
            </div>
            <div className="recommended-inline" aria-label={`Recommended uses for ${tool.name}`}>
              <strong>Recommended for</strong>
              <div>
                {tool.recommendedFor.map((item) => <span key={item}><CheckCircle2 size={14} /> {item}</span>)}
              </div>
            </div>
          </div>
        </div>

        <nav className="tool-page-jump-nav" aria-label={`${tool.name} page sections`}>
          <a href="#workspace">Try the tool</a>
          <a href="#how-it-works">How it works</a>
          <a href="#tool-faq">FAQ</a>
          <a href="#related-guides">Related guides</a>
        </nav>

        <div id="workspace" className="tool-workspace-frame">{isFree ? <FreeImageWorkspace tool={tool} /> : <ImageWorkspace tool={tool} />}</div>

        <section className="tool-content-section">
          <div className="section-heading centered">
            <p className="eyebrow">Why use this workflow?</p>
            <h2>{isFree ? 'A focused browser utility, not an AI-credit workflow.' : 'Built around the image problem, not a generic filter.'}</h2>
          </div>
          <div className="tool-benefit-grid">
            {tool.benefits.map((benefit, index) => (
              <article key={benefit.title}>
                <span>0{index + 1}</span>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="how-it-works" className="tool-content-section tool-how-section">
          <div className="tool-how-copy">
            <p className="eyebrow">How to use {tool.shortName.toLowerCase()}</p>
            <h2>A simple three-step workflow.</h2>
            <p>Start with the strongest source available, use the smallest effective setting and review the result against the original before professional use.</p>
          </div>
          <div className="tool-how-list">
            {tool.howItWorks.map((step, index) => (
              <article key={step.title}>
                <span>{index + 1}</span>
                <div><h3>{step.title}</h3><p>{step.description}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="tool-content-section tool-tips-section">
          <div className="tool-tips-card">
            <span className="tool-icon"><Lightbulb size={21} /></span>
            <div>
              <p className="eyebrow">Professional tips</p>
              <h2>Get a more natural result.</h2>
            </div>
            <ul>
              {tool.tips.map((tip) => <li key={tip}><CheckCircle2 size={17} /> {tip}</li>)}
            </ul>
          </div>
          <div className="tool-limit-card">
            <Sparkles size={24} />
            {isFree ? <>
              <h3>Free utilities process visible pixels in your browser.</h3>
              <p>No AI credits are used. Keep the original file because resizing, cropping, conversion and compression can change dimensions, quality, metadata or transparency.</p>
            </> : <>
              <h3>AI improves images. It does not reveal hidden facts.</h3>
              <p>Strong restoration and generative enhancement can estimate missing detail. Keep the original, compare carefully and avoid treating reconstructed details as evidence.</p>
            </>}
          </div>
        </section>

        <section id="tool-faq" className="tool-content-section tool-faq-section">
          <div className="section-heading">
            <p className="eyebrow">{tool.name} FAQ</p>
            <h2>Common questions before processing.</h2>
          </div>
          <div className="tool-faq-list">
            {tool.faqs.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section id="related-guides" className="related-guides tool-related-guides">
          <div className="section-heading">
            <p className="eyebrow">Learn before processing</p>
            <h2>Guides for better {tool.shortName.toLowerCase()} results</h2>
          </div>
          <div className="related-guide-grid">
            {relatedGuides.map((guide) => (
              <Link key={guide.slug} to={`/guides/${guide.slug}`}>
                <span>{guide.category}</span>
                <strong>{guide.title}</strong>
              </Link>
            ))}
          </div>
        </section>

        <div className="related-tools">
          <div className="section-heading">
            <p className="eyebrow">Continue your workflow</p>
            <h2>{isFree ? 'Related free image utilities' : 'Related AI image tools'}</h2>
          </div>
          <div className="related-grid">
            {related.map((item) => {
              const RelatedIcon = item.icon;
              return (
                <Link key={item.slug} to={`/tools/${item.slug}`}>
                  <span className="tool-icon"><RelatedIcon size={19} /></span>
                  <div><strong>{item.name}</strong><p>{item.description}</p></div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
